import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, ButtonBase, Skeleton, Typography, alpha } from "@mui/material";
import { Business, Groups, Lock, MenuBook, Shield, SvgIconComponent } from "@mui/icons-material";
import * as Sentry from "@sentry/react";
import { RootState } from "@store/store";
import { setLoanApplication, setLoanStatus } from "@store/slices/registerSlice";
import {
  setExpedienteDictamen,
  setExpedienteEmpresa,
  setExpedienteTabsReady,
  setExpedienteTabs,
} from "@store/slices/expedienteSlice";
import { useLazyGetDocumentAppIdQuery } from "@api/documents";
import { useLazyGetLoanApplicationByIdQuery } from "@api/applications";
import { useLazyGetPersonsQuery } from "@api/personasApi";
import {
  useLazyGetCivilStatusQuery,
  useLazyGetMatrimonialRegimesQuery,
  useLazyGetTabsConfigQuery,
} from "@api/catalogs";
import { useLazyGetGuaranteesDataQuery } from "@api/garantiasApi";
import { BofuTabDocument, BofuTabObject, BofuTabStatus } from "@interfaces/response";
import { BOFU_TABS_CONFIG, DOCUMENTS_CATALOG, STATUS_CATALOG, TAX_SYSTEM_TYPE } from "@helpers/constantes";
import { identifyPersons } from "@helpers/funciones";
import {
  filterDocumentsByGuarantee,
  generateDocumentConfig,
  generateGuaranteeDocs,
  generateTabLabel,
  identityDocumentsByPerson,
  initializeBofuTabsObject,
  mapBofuTabEmpresaForms,
  validateDocumentStructure,
} from "./functions";

export type TabBofuV2Id = "dictamen" | "empresa" | "garantia" | "personas";

interface Props {
  active: TabBofuV2Id;
}

const SURFACE_2 = "tokens.surface2";
const BORDER = "divider";

/**
 * Escala de la barra. `lg` es el `.exp-tab` de `styles.css` tal cual; ese spec asume las 3 pestañas
 * de `.exp-tabs-row` (`repeat(3, 1fr)`), así que con una cuarta pestaña —la de garantía, que el
 * diseño no contempla— a cada tab le toca ~25% del ancho y el contenido se aprieta contra el
 * círculo. `md` es la misma barra reducida para ese caso.
 *
 * El tamaño del texto no entra aquí: label y motivo del bloqueo usan los `variant` del theme
 * (`body2` / `body3`) en las dos escalas, en vez de los 14px / 11.5px sueltos del CSS.
 */
const SIZES = {
  lg: { circulo: 44, icono: 18, padding: "14px 16px", gapTab: "12px", gapBarra: "10px" },
  md: { circulo: 34, icono: 16, padding: "10px 12px", gapTab: "10px", gapBarra: "8px" },
};

/** A partir de 4 pestañas el spec del diseño ya no da el ancho. */
const MAX_TABS_LG = 3;

/**
 * Alto del skeleton. Es fijo porque mientras carga todavía no se sabe cuántas pestañas trae el
 * expediente, así que no hay escala que consultar; corresponde al alto de `md`.
 */
const ALTO_SKELETON = 76;

/** Motivo del bloqueo: el expediente abre las secciones de personas al validar el dictamen. */
const MOTIVO_BLOQUEO = "Disponible al validar tu dictamen jurídico";

/** Paleta del punto de estado de cada tab. */
const STATUS_PALETTE: Record<BofuTabStatus, "warning" | "info" | "success"> = {
  incomplete: "warning",
  cargado: "info",
  validado: "success",
  no_aplica: "success",
};

/** Punto de estado del tab, con el halo del mismo tono que el diseño (`exp-tab-dot`). */
const StatusDot = ({ status }: { status: BofuTabStatus }) => (
  <Box
    sx={(theme) => ({
      width: 8,
      height: 8,
      borderRadius: "50%",
      flexShrink: 0,
      bgcolor: theme.palette[STATUS_PALETTE[status]].main,
      boxShadow: `0 0 0 3px ${alpha(theme.palette[STATUS_PALETTE[status]].main, 0.22)}`,
    })}
  />
);

interface TabVisual {
  id: TabBofuV2Id;
  label: string;
  /** El componente, no el elemento: el tamaño lo pone la escala al renderizar. */
  Icon: SvgIconComponent;
  /** Ruta del wizard nuevo; sin ella se usa la del store (que apunta al expediente legacy). */
  path?: string;
}

const VISUALES: Record<TabBofuV2Id, TabVisual> = {
  dictamen: { id: "dictamen", label: "Dictamen", Icon: MenuBook, path: "expediente" },
  empresa: { id: "empresa", label: "Empresa", Icon: Business, path: "expediente?tab=empresa" },
  garantia: { id: "garantia", label: "Garantía", Icon: Shield, path: "expediente?tab=garantia" },
  personas: { id: "personas", label: "Personas relacionadas", Icon: Groups, path: "expediente?tab=personas" },
};

interface TabRender extends TabVisual {
  status: BofuTabStatus | undefined;
  disabled: boolean;
  /** Ruta legacy del store, para los tabs que aún no tienen pantalla nueva. */
  legacyUrl: string;
}

/**
 * Barra de secciones del expediente con el diseño del wizard nuevo (`exp-tabs`), alimentada con
 * el estado real del expediente.
 *
 * Trae la misma lógica que su vecino `TabBofu.tsx`: arma la estructura del expediente en el store
 * (documentos, personas, garantías), calcula el estado de cada sección y dispara los dos efectos
 * de negocio del tramo (generar la solicitud de crédito y mandar a dictamen jurídico).
 *
 * TODO: esa lógica está **duplicada** del `TabBofu` legacy, que sigue en uso en las pantallas del
 * expediente viejo. Cualquier arreglo hay que hacerlo en los dos lados hasta que se extraiga a un
 * hook compartido.
 *
 * Diferencia con el legacy: colapsa Aval, Propietario real y Representante en un solo tab
 * "Personas relacionadas", como el diseño.
 */
export const TabBofuV2 = ({ active }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [isSuccessData, setIsSuccessData] = useState<boolean>(false);
  const [localTabs, setLocalTabs] = useState<BofuTabObject>(initializeBofuTabsObject());

  const clientDetails = useSelector((state: RootState) => state.register.personal_data);
  const applicationData = useSelector((state: RootState) => state.register.application);

  const [getDocumentsByAppId] = useLazyGetDocumentAppIdQuery();
  const [getPersons] = useLazyGetPersonsQuery();
  const [getLoanAplication] = useLazyGetLoanApplicationByIdQuery();
  const [getTabsConfig] = useLazyGetTabsConfigQuery();
  const [getGuaranteesData] = useLazyGetGuaranteesDataQuery();
  const [getEstadoCivil] = useLazyGetCivilStatusQuery();
  const [getMatrimonialRegimen] = useLazyGetMatrimonialRegimesQuery();

  const generateTabsStatusLabel = () => {
    let dictamen_new_status: BofuTabStatus | undefined;
    let empresa_new_status: BofuTabStatus | undefined;
    let garantia_new_status: BofuTabStatus | undefined;

    if (localTabs.dictamen.is_active) {
      dictamen_new_status = generateTabLabel({ tab_documents: localTabs.dictamen.documents, other_documents: [] });
    }

    if (localTabs.empresa.is_active) {
      empresa_new_status = generateTabLabel({
        tab_documents: localTabs.empresa.documents,
        other_documents: [],
        forms: [
          applicationData.loan_application_information_status,
          applicationData.references_status,
          applicationData.bank_information_status,
        ],
      });
    }

    if (localTabs.garantia.is_active) {
      garantia_new_status = localTabs.garantia.items.length
        ? generateTabLabel({
            tab_documents: localTabs.garantia.items.flatMap((item) => item.documents),
            other_documents: [],
          })
        : "no_aplica";
    }

    setLocalTabs((prev) => ({
      ...prev,
      dictamen: { ...prev.dictamen, tab_status: dictamen_new_status },
      empresa: { ...prev.empresa, tab_status: empresa_new_status },
      garantia: { ...prev.garantia, tab_status: garantia_new_status },
    }));

    dispatch(setExpedienteDictamen({ ...localTabs.dictamen, tab_status: dictamen_new_status }));
    dispatch(setExpedienteEmpresa({ ...localTabs.empresa, tab_status: empresa_new_status }));
  };

  const generateTabsStructure = async () => {
    try {
      setIsSuccessData(false);
      dispatch(setExpedienteTabsReady(false));

      const [
        tabs_config,
        loan_application,
        persons,
        documents,
        list_estado_civil,
        list_matrimonial_regimen,
        guarantee_data,
      ] = await Promise.all([
        getTabsConfig({ taxcode: applicationData.type }).unwrap(),
        getLoanAplication(applicationData.id).unwrap(),
        getPersons({ loanApplicationId: applicationData.id }).unwrap(),
        getDocumentsByAppId({ loanApplicationId: applicationData.id, status: true }).unwrap(),
        getEstadoCivil().unwrap(),
        getMatrimonialRegimen().unwrap(),
        getGuaranteesData({ loan_application_id: applicationData.id }).unwrap(),
      ]);

      // identificar si es una solicitud pm o pfae
      const isPFAE = loan_application?.tax_systems?.tax_system_type === TAX_SYSTEM_TYPE.PFAE.code;

      // validar si la solicitud fue rechazada por documentacion
      const { rechazo_expediente } = STATUS_CATALOG;
      if (loan_application?.status_catalog?.code === rechazo_expediente.code) {
        dispatch(setLoanStatus(rechazo_expediente.code));
        navigate(rechazo_expediente.url);
        return;
      }

      // identificar las personas
      const {
        representante,
        representantes,
        aval,
        conyuges,
        propietarios,
        segundos_avales: lista_avales_adicionales,
      } = identifyPersons(persons, list_estado_civil, list_matrimonial_regimen);

      // obtener los documentos por persona
      const { docs_account, docs_aval, docs_guarantee } = identityDocumentsByPerson({
        documents,
        representante,
        aval,
        client_details: clientDetails,
        propietarios,
        representantes,
        avales_adicionales: lista_avales_adicionales,
        conyuges,
      });

      const { solicitud_credito } = DOCUMENTS_CATALOG;

      const bofu_tabs_obj: BofuTabObject = initializeBofuTabsObject();

      /* NOTAS:
      - dictamen: pfae no tiene dictamen...
      */

      tabs_config.forEach((tabItem) => {
        // validar que los documentos tengan la estructura correcta
        const valid_tab_documents: BofuTabDocument[] = [];
        (tabItem.documents || [])
          .filter((item) => validateDocumentStructure(item))
          .filter((item) => filterDocumentsByGuarantee(item, guarantee_data)) // filtrar por el tipo de garantia, si se requiere
          .forEach((docItem) => {
            valid_tab_documents.push(
              generateDocumentConfig({
                docItem,
                docs_account,
                docs_aval,
                clientDetailsId: clientDetails.id,
                aval,
              }),
            );
          });

        // ordernar los documentos por el order
        valid_tab_documents.sort((a, b) => (a.order || 0) - (b.order || 0));

        if (tabItem.tabs_bofu.code === BOFU_TABS_CONFIG.tab_dictamen.code) {
          bofu_tabs_obj.dictamen.order = tabItem.order;
          bofu_tabs_obj.dictamen.is_disabled = false;
          bofu_tabs_obj.dictamen.is_active = true;
          bofu_tabs_obj.dictamen.documents = valid_tab_documents;

          if (isPFAE) {
            bofu_tabs_obj.dictamen.is_active = false;
          }
        } else if (tabItem.tabs_bofu.code === BOFU_TABS_CONFIG.tab_empresa.code) {
          const doc_solicitud_credito = docs_account.find((item) => item.file_code === solicitud_credito.code);
          bofu_tabs_obj.empresa.order = tabItem.order;
          bofu_tabs_obj.empresa.is_disabled = false;
          bofu_tabs_obj.empresa.is_active = true;
          bofu_tabs_obj.empresa.documents = valid_tab_documents;
          bofu_tabs_obj.empresa.forms = mapBofuTabEmpresaForms(tabItem.forms);
          bofu_tabs_obj.empresa.doc_solicitud_credito = doc_solicitud_credito;

          if (isPFAE) {
            bofu_tabs_obj.empresa.url = "/producto/creditosimple/avance_expediente_digitalPFAE";
          }
        } else if (tabItem.tabs_bofu.code === BOFU_TABS_CONFIG.tab_personas.code) {
          bofu_tabs_obj.personas.order = tabItem.order;
          bofu_tabs_obj.personas.is_active = true;
          bofu_tabs_obj.personas.is_disabled = isPFAE ? false : !loan_application.decision_validated;
          bofu_tabs_obj.personas.documents = valid_tab_documents;
        } else if (tabItem.tabs_bofu.code === BOFU_TABS_CONFIG.tab_garantia.code) {
          bofu_tabs_obj.garantia.order = tabItem.order;
          bofu_tabs_obj.garantia.is_disabled = false;
          bofu_tabs_obj.garantia.is_active = true;
          bofu_tabs_obj.garantia.items = [];

          guarantee_data.forEach((garantiaItem) => {
            bofu_tabs_obj.garantia.items.push({
              guarantee: garantiaItem,
              documents: generateGuaranteeDocs({
                clientDetailsId: clientDetails.id,
                guaranteeItem: garantiaItem,
                valid_documents: valid_tab_documents,
                documents_uploaded: docs_guarantee,
              }),
            });
          });

          // ordenar por 2 parametros: deben tener documentos y orden alfabetico
          bofu_tabs_obj.garantia.items.sort((a, b) => {
            if (a.documents.length === 0 && b.documents.length > 0) {
              return 1;
            }
            if (a.documents.length > 0 && b.documents.length === 0) {
              return -1;
            }
            return a.guarantee.guarantee_type?.name?.localeCompare(b.guarantee.guarantee_type?.name || "");
          });
        }
      });

      setLocalTabs(bofu_tabs_obj);
      dispatch(setExpedienteDictamen(bofu_tabs_obj.dictamen));
      dispatch(setExpedienteEmpresa(bofu_tabs_obj.empresa));
      dispatch(
        setExpedienteTabs({
          dictamen: bofu_tabs_obj.dictamen,
          empresa: bofu_tabs_obj.empresa,
          garantia: bofu_tabs_obj.garantia,
          personas: bofu_tabs_obj.personas,
        }),
      );
      dispatch(setExpedienteTabsReady(true));
      dispatch(setLoanApplication(loan_application));
      setIsSuccessData(true);
    } catch (error) {
      console.log("error al generar las estructuras de los tabs", error);
      Sentry.captureException(error, { extra: { component: "TabBofuV2-generateTabsStructure" } });
    }
  };

  useEffect(() => {
    generateTabsStatusLabel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    localTabs.dictamen.documents,
    localTabs.empresa.documents,
    localTabs.garantia.items,
    applicationData.decision_validated,
    applicationData.loan_application_information_status,
    applicationData.references_status,
    applicationData.bank_information_status,
  ]);

  useEffect(() => {
    generateTabsStructure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const { dictamen, empresa, garantia, personas: personasTab } = localTabs;

  const tabs: TabRender[] = [
    ...(dictamen.is_active
      ? [{ ...VISUALES.dictamen, status: dictamen.tab_status, disabled: dictamen.is_disabled, legacyUrl: dictamen.url }]
      : []),
    ...(empresa.is_active
      ? [{ ...VISUALES.empresa, status: empresa.tab_status, disabled: empresa.is_disabled, legacyUrl: empresa.url }]
      : []),
    ...(garantia.is_active
      ? [{ ...VISUALES.garantia, status: garantia.tab_status, disabled: garantia.is_disabled, legacyUrl: garantia.url }]
      : []),
    ...(personasTab.is_active
      ? [
          {
            ...VISUALES.personas,
            status: personasTab.tab_status,
            disabled: personasTab.is_disabled,
            legacyUrl: personasTab.url,
          },
        ]
      : []),
  ];

  if (!isSuccessData) {
    return <Skeleton variant="rectangular" sx={{ height: ALTO_SKELETON, borderRadius: 2, mb: 3 }} />;
  }

  const size = SIZES[tabs.length > MAX_TABS_LG ? "md" : "lg"];

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: `repeat(${tabs.length}, 1fr)` },
        gap: size.gapBarra,
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: BORDER,
        borderRadius: "12px",
        p: "6px",
        mb: 3,
      }}
    >
      {tabs.map((tab) => {
        const isActive = tab.id === active;

        return (
          <Box key={tab.id} sx={{ cursor: tab.disabled ? "not-allowed" : "default", width: "100%" }}>
            <ButtonBase
              disabled={tab.disabled || isActive}
              onClick={() => navigate(tab.path ? `/v2/producto/creditosimple/${tab.path}` : tab.legacyUrl)}
              data-testid={`expediente-tab-${tab.id}`}
              sx={(theme) => ({
                display: "flex",
                alignItems: "center",
                width: "100%",
                justifyContent: "flex-start",
                gap: size.gapTab,
                p: size.padding,
                border: "1px solid transparent",
                borderRadius: "9px",
                textAlign: "left",
                color: "text.disabled",
                transition: "background-color .14s ease, color .14s ease, box-shadow .14s ease, border-color .14s ease",
                "&:hover": { color: "text.secondary", bgcolor: alpha("#FFFFFF", 0.6) },
                ...(isActive && {
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  borderColor: "primary.main",
                  boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)}, 0 4px 14px -4px ${alpha(
                    theme.palette.primary.main,
                    0.28,
                  )}`,
                  "&.Mui-disabled": { color: "text.primary" },
                }),
                ...(tab.disabled && {
                  bgcolor: SURFACE_2,
                  borderColor: BORDER,
                  cursor: "not-allowed",
                  "&.Mui-disabled": { opacity: 0.55, color: "text.disabled", cursor: "not-allowed" },
                }),
              })}
            >
              <Box
                sx={(theme) => ({
                  width: size.circulo,
                  height: size.circulo,
                  borderRadius: "50%",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  bgcolor: SURFACE_2,
                  border: "1px solid",
                  borderColor: BORDER,
                  color: "text.disabled",
                  transition: "background-color .2s ease, color .2s ease, border-color .2s ease, transform .25s ease",
                  ...(isActive && {
                    background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.9)} 0%, ${
                      theme.palette.primary.main
                    } 100%)`,
                    borderColor: "primary.main",
                    color: "#fff",
                    transform: "scale(1.04)",
                    boxShadow: `0 1px 0 rgba(255, 255, 255, 0.18) inset, 0 4px 12px -2px ${alpha(
                      theme.palette.primary.main,
                      0.4,
                    )}`,
                  }),
                  ...(tab.disabled && { borderStyle: "dashed", borderColor: "#C8C5BE" }),
                })}
              >
                {tab.disabled ? <Lock sx={{ fontSize: size.icono - 2 }} /> : <tab.Icon sx={{ fontSize: size.icono }} />}
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0, flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    letterSpacing: "-0.01em",
                    color: isActive ? "primary.dark" : "inherit",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {tab.label}
                </Typography>
                {tab.disabled && (
                  <Typography
                    variant="body3"
                    sx={{
                      color: "text.disabled",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {MOTIVO_BLOQUEO}
                  </Typography>
                )}
              </Box>

              {!tab.disabled && tab.status && <StatusDot status={tab.status} />}
            </ButtonBase>
          </Box>
        );
      })}
    </Box>
  );
};
